/**
 * NA-283: the editor top-bar Delete, for the lesson and course editors.
 *
 * A dedicated danger button rather than an overflow menu: the ticket requires
 * Delete to stay *visible* when ineligible (a menu hides it), there is only one
 * action to place, and no editor kebab pattern exists to extend.
 *
 * Standard context only — client contexts never offer Delete, matching the list
 * kebabs, which omit the item entirely rather than disabling it.
 *
 * Two containers over one presentation: each editor reaches for its own
 * domain's delete mutation, so the lesson editor never pulls in course APIs.
 */

import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, type DeleteEligibility } from '@course-authoring/shared-types';
import { useDeleteLessonMutation } from '../api/courseApi';
import { useDeleteCourseMutation } from '../api/courseRecordApi';
import { canDelete, describeDeleteBlock } from '../utils/deleteEligibility';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface EditorDeleteActionProps {
  contentId: string;
  /** Shown in the confirmation dialog. */
  title: string;
  eligibility: DeleteEligibility | null | undefined;
  /** False in a client account context, where Delete is not offered at all. */
  isStandardContext: boolean;
  /** Suppressed while the editor is busy with a blocking operation. */
  disabled?: boolean;
}

interface DeleteActionViewProps extends EditorDeleteActionProps {
  kind: 'lesson' | 'course';
  isPending: boolean;
  errorMessage: string | null;
  allowRetry: boolean;
  onOpen: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  isConfirmOpen: boolean;
}

function DeleteActionView({
  kind,
  title,
  eligibility,
  isStandardContext,
  disabled = false,
  isPending,
  errorMessage,
  allowRetry,
  isConfirmOpen,
  onOpen,
  onConfirm,
  onCancel,
}: DeleteActionViewProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  if (!isStandardContext) return null;

  const blockedReason = describeDeleteBlock(eligibility ?? undefined);
  const deletable = canDelete(eligibility ?? undefined);
  const label = kind === 'lesson' ? 'Lesson' : 'Course';

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        disabled={!deletable || disabled}
        title={blockedReason ?? undefined}
        aria-label={blockedReason ? `Delete ${label} — ${blockedReason}` : `Delete ${label}`}
        style={{
          ...styles.deleteButton,
          ...(!deletable || disabled ? styles.deleteButtonDisabled : {}),
        }}
      >
        Delete
      </button>
      <DeleteConfirmModal
        isOpen={isConfirmOpen}
        title={`Delete ${label}?`}
        itemName={title || `Untitled ${label}`}
        isDeleting={isPending}
        errorMessage={errorMessage}
        allowRetry={allowRetry}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  );
}

/** Shared open/error state and post-delete navigation for both containers. */
function useDeleteActionState(kind: 'lesson' | 'course') {
  const navigate = useNavigate();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [allowRetry, setAllowRetry] = useState(true);

  const onSuccess = useCallback(() => {
    setIsConfirmOpen(false);
    setErrorMessage(null);
    setAllowRetry(true);
    navigate(kind === 'lesson' ? '/lessons' : '/courses');
  }, [navigate, kind]);

  // Errors keep the dialog open: unlike the lists there is no row behind it to
  // flip to disabled, so the dialog is where the reason has to land. A 409 is
  // an eligibility fact, not a hiccup — lock the confirm so the author can't
  // hammer a request that cannot succeed. The mutation has already refreshed
  // the detail read, so the button behind the dialog is disabled too.
  const onError = useCallback((error: { message: string; status?: number }) => {
    setErrorMessage(error.message || `Failed to delete ${kind}`);
    setAllowRetry(error.status !== 409);
  }, [kind]);

  const clearError = useCallback(() => {
    setErrorMessage(null);
    setAllowRetry(true);
  }, []);

  const open = useCallback(() => {
    clearError();
    setIsConfirmOpen(true);
  }, [clearError]);

  return {
    isConfirmOpen, errorMessage, allowRetry, clearError, setIsConfirmOpen, onSuccess, onError, open,
  };
}

export function LessonDeleteAction(props: EditorDeleteActionProps) {
  const state = useDeleteActionState('lesson');
  const { mutate, isPending } = useDeleteLessonMutation({
    onSuccess: state.onSuccess,
    onError: state.onError,
  });

  return (
    <DeleteActionView
      {...props}
      kind="lesson"
      isPending={isPending}
      errorMessage={state.errorMessage}
      allowRetry={state.allowRetry}
      isConfirmOpen={state.isConfirmOpen}
      onOpen={state.open}
      onConfirm={() => { state.clearError(); mutate(props.contentId); }}
      onCancel={() => { if (!isPending) { state.setIsConfirmOpen(false); state.clearError(); } }}
    />
  );
}

export function CourseDeleteAction(props: EditorDeleteActionProps) {
  const state = useDeleteActionState('course');
  const { mutate, isPending } = useDeleteCourseMutation({
    onSuccess: state.onSuccess,
    onError: state.onError,
  });

  return (
    <DeleteActionView
      {...props}
      kind="course"
      isPending={isPending}
      errorMessage={state.errorMessage}
      allowRetry={state.allowRetry}
      isConfirmOpen={state.isConfirmOpen}
      onOpen={state.open}
      onConfirm={() => { state.clearError(); mutate(props.contentId); }}
      onCancel={() => { if (!isPending) { state.setIsConfirmOpen(false); state.clearError(); } }}
    />
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, CSSProperties> {
  return {
    deleteButton: {
      padding: '8px 16px',
      fontSize: '14px',
      color: tokens.ui.errorText,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.ui.errorText}`,
      borderRadius: '6px',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    deleteButtonDisabled: {
      color: tokens.text.subtle,
      borderColor: tokens.borders.default,
      cursor: 'not-allowed',
    },
  };
}
